// hooks/useApplicationData.js
import axios from 'axios';
import { useDispatch } from 'react-redux';
import {
  setAppetiteData,
  selectedApplicationData,
  setTotalApplications,
  setUniqueValues,
  setAppId,
  setSelectedAppIdName,
  setSelectedApplicationIdData
} from '../store/ApplicationSlice';
import { useNavigate } from 'react-router-dom';
import {
  fetchAppUniqueValues,
  fetchTotalApplications,
  fetchApplicationDataByIdAndName
} from '../api/userService';

const useApplicationData = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchApplicationData = async (
    accessToken,
    refreshToken,
    page = 1,
    limit = 15
  ) => {
    const skip = (page - 1) * limit;

    const headers = {
      'X-Access-Token': accessToken,
      'X-Refresh-Token': refreshToken,
      Accept: 'application/json'
    };

    // Extract application id from URL if available
    const pathParts = window.location.pathname.split('/');
    const dynamicAppId = pathParts[pathParts.length - 1] || '';
    const hasDynamicId =
      dynamicAppId && dynamicAppId.toLowerCase().startsWith('cipilotiaiapp');

    const baseUrl =
      'https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/application_filter';
    const url = hasDynamicId
      ? `${baseUrl}?application_id=${dynamicAppId}`
      : `${baseUrl}?skip=${skip}&limit=${limit}`;

    try {
      const response = await axios.get(url, { headers });

      if (!response.data) {
        throw new Error('No data received from the server');
      }

      dispatch(setAppetiteData(response.data));

      const applicationData = hasDynamicId
        ? Array.isArray(response.data) && response.data.length > 0
          ? response.data[0]
          : null
        : response.data[0];

      if (!applicationData) {
        console.error('No valid application data found');
        navigate('/');
        return;
      }

      // Update application data in Redux
      dispatch(selectedApplicationData(applicationData));
      dispatch(setSelectedAppIdName(applicationData.customer_name));
      dispatch(setAppId(applicationData.application_id));

      // Fetch additional details if we have the required data
      if (applicationData.customer_name && applicationData.application_id) {
        try {
          const detailsResponse = await fetchApplicationDataByIdAndName(
            applicationData.customer_name,
            applicationData.application_id
          );
          if (detailsResponse) {
            dispatch(setSelectedApplicationIdData(detailsResponse));
          }
        } catch (detailError) {
          console.error('Error fetching application details:', detailError);
          // Continue execution even if details fetch fails
        }
      }
    } catch (error) {
      console.error('Error in fetchApplicationData:', error);
      if (error.response?.status === 401) {
        navigate('/');
      } else {
        // For other errors, we might want to show an error state rather than redirecting
        console.error('Error fetching application data:', error);
      }
    }
  };

  const fetchTotalApplicationsCount = async (accessToken, refreshToken) => {
    try {
      const response = await fetchTotalApplications(accessToken, refreshToken);
      dispatch(setTotalApplications(response.total));
    } catch (error) {
      navigate('/');
    }
  };

  const fetchUniqueValues = async (accessToken, refreshToken) => {
    try {
      const response = await fetchAppUniqueValues(accessToken, refreshToken);
      dispatch(setUniqueValues(response));
    } catch (error) {
      navigate('/');
    }
  };

  return {
    fetchApplicationData,
    fetchTotalApplicationsCount,
    fetchUniqueValues
  };
};

export default useApplicationData;
